import type { FlexFamily, MachineCatalogue } from './priceBook'

/**
 * Turns a provider-neutral size into a provider's machine.
 *
 * The rule is the one a person would apply with the price list open: the
 * cheapest machine with at least the vCPUs and the memory asked for. Asking for
 * 2 vCPUs and 16 GB therefore lands on a memory-optimised size where one exists,
 * and on a bigger general-purpose one where it does not.
 */

export interface Size {
  readonly vcpu: number
  readonly memoryGb: number
}

export interface Machine extends Size {
  readonly name: string
  readonly hourly: number
}

/** `null` when nothing the provider sells is that large. */
export function matchMachine(catalogue: MachineCatalogue, want: Size): Machine | null {
  const candidates: Machine[] = [
    ...catalogue.fixed.filter((m) => m.vcpu >= want.vcpu && m.memoryGb >= want.memoryGb),
    ...catalogue.flex.flatMap((family) => sizeFlex(family, want) ?? []),
  ]
  // Ties go to the fewer vCPUs, then to catalogue order, so the pick is stable.
  return candidates.reduce<Machine | null>((best, machine) => {
    if (!best || machine.hourly < best.hourly) return machine
    if (machine.hourly === best.hourly && machine.vcpu < best.vcpu) return machine
    return best
  }, null)
}

/**
 * The smallest shape of a made-to-order family that fits: vCPUs rounded up to
 * what is sold, more of them if the memory would otherwise exceed the family's
 * ratio, and memory topped up to the family's minimum per vCPU.
 */
export function sizeFlex(family: FlexFamily, want: Size): Machine | null {
  const step = family.vcpuStep
  const roundUp = (value: number): number => Math.ceil(value / step) * step
  const vcpu = Math.max(roundUp(want.vcpu), roundUp(Math.ceil(want.memoryGb / family.maxGbPerVcpu)))
  if (vcpu > family.maxVcpu) return null
  const memoryGb = Math.max(want.memoryGb, Math.ceil(vcpu * family.minGbPerVcpu))
  return {
    name: family.name,
    vcpu,
    memoryGb,
    hourly: vcpu * family.perVcpuHour + memoryGb * family.perGbHour,
  }
}
